import { cn } from '@/lib/utils'
import { type ReactNode } from 'react'
import { useNavigate, useRouter, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import './FixedMobileTopBar.css'

interface FixedMobileTopBarIconProps {
  children: ReactNode
  className?: string
}

export function FixedMobileTopBarIcon({ children, className }: FixedMobileTopBarIconProps) {
  return (
    <div
      className={cn('flex h-10 w-10 items-center justify-center rounded-full bg-white/70 backdrop-blur-sm', className)}
    >
      {children}
    </div>
  )
}

interface FixedMobileTopBarProps {
  left?: ReactNode
  right?: ReactNode
  className?: string
  variant?: 'default' | 'compact' | 'solid'
}

export function FixedMobileTopBar({ left, right, className, variant = 'default' }: FixedMobileTopBarProps) {
  return (
    <div
      className={cn(
        'fixed-mobile-top-bar fixed inset-x-0 top-0 z-40 container mx-auto flex items-center justify-between pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 lg:hidden',
        variant === 'compact' && 'fixed-mobile-top-bar--compact',
        variant === 'solid' && 'fixed-mobile-top-bar--solid',
        className,
      )}
    >
      {left ?? <div className="h-10 w-10" />}
      <Link to="/" className="fixed-mobile-top-bar__logo -ml-2 flex flex-col items-center">
        <img src="/img/logo_matevolno.svg" alt="logo" className="-my-5 -mb-7 scale-75" />
        {/* Kept out of snippet selection — as the first text on every page it
            was what Google put in front of the meta description. */}
        <span
          data-nosnippet
          className="mt-1 text-center text-[11px] leading-tight font-medium tracking-tight whitespace-nowrap text-slate-500"
        >
          Last-minute úniky na příštích 14 dní.
        </span>
      </Link>
      {right ?? <div className="h-10 w-10" />}
    </div>
  )
}

interface NavigateBackProps {
  /** Target URL for view transition navigation. If not provided, uses history.back() */
  to?: string
}

export function NavigateBack({ to }: NavigateBackProps) {
  const router = useRouter()
  const navigate = useNavigate()

  const handleBack = () => {
    if (to) {
      void navigate({ to, viewTransition: true })
    } else if (router.history.canGoBack()) {
      // Not every supported browser has view transitions yet (the DOM typings
      // claim it is always there).
      if ('startViewTransition' in document) {
        document.startViewTransition(() => window.history.back())
      } else {
        window.history.back()
      }
    } else {
      // No in-app history (e.g. landed directly from an ad) – go home instead of leaving the site
      void navigate({ to: '/', viewTransition: true })
    }
  }

  return (
    <button onClick={handleBack} aria-label="Zpět" className="-m-2 touch-manipulation p-2">
      <FixedMobileTopBarIcon>
        <ArrowLeftIcon className="h-6 w-6 text-slate-700" />
      </FixedMobileTopBarIcon>
    </button>
  )
}
